/**
 * Utility functions for managing prospect details modal URLs
 * Automatically handles URL construction based on the current route context
 */

export type ProspectDetailsRouteInfo = {
  basePath: string;
  sequenceId?: string | null;
  hashId?: string | null;
  viewType:
    | 'prospects-list'
    | 'prospects-kanban'
    | 'sequence'
    | 'leads'
    | 'conversations'
    | 'tasks'
    | null;
};

/**
 * Extracts route information from the current pathname
 * Handles routes:
 * - /crm/list/:id/:tab
 * - /crm/kanban/:id/:tab
 * - /sequence/:sequenceId/prospects/:id/:tab
 * - /sequence/:sequenceId/emails/:id/:tab
 * - /v2/leads/:tab/:subtab/:id/:tab (dynamic - supports any tab/subtab combination)
 * - /leads
 * - /conversations
 * - /tasks
 */
export const getProspectDetailsRouteInfo = (
  pathname: string,
): ProspectDetailsRouteInfo => {
  // Check for sequence tab routes:
  // - /sequence/:sequenceId/prospects/:prospectId/:tab
  // - /sequence/:sequenceId/emails/:prospectId/:tab
  const sequenceTabMatch = pathname.match(
    /^\/sequence\/(\d+)\/(prospects|emails)(?:\/(\d+)(?:\/([^/?]+))?)?/,
  );
  if (sequenceTabMatch) {
    return {
      basePath: `/sequence/${sequenceTabMatch[1]}/${sequenceTabMatch[2]}`,
      sequenceId: sequenceTabMatch[1],
      viewType: 'sequence',
    };
  }

  // Check for prospects list route: /crm/list/:id/:tab
  const prospectsListMatch = pathname.match(
    /^\/crm\/list(?:\/(\d+)(?:\/([^/?]+))?)?/,
  );
  if (prospectsListMatch) {
    return {
      basePath: '/crm/list',
      sequenceId: null,
      viewType: 'prospects-list',
    };
  }

  // Check for prospects kanban route: /crm/kanban/:id/:tab
  const prospectsKanbanMatch = pathname.match(
    /^\/crm\/kanban(?:\/(\d+)(?:\/([^/?]+))?)?/,
  );
  if (prospectsKanbanMatch) {
    return {
      basePath: '/crm/kanban',
      sequenceId: null,
      viewType: 'prospects-kanban',
    };
  }

  // Check for v2 leads routes: /v2/leads/:tab/:subtab or /v2/leads/:tab/:subtab/:id/:tab
  // This pattern dynamically matches any tab and subtab (e.g., people/total, company/saved, etc.)
  const v2LeadsMatch = pathname.match(
    /^\/v2\/leads\/([^/]+)\/([^/]+)(?:\/(\d+)(?:\/([^/?]+))?)?/,
  );
  if (v2LeadsMatch) {
    const leadType = v2LeadsMatch[1]; // any tab type (people, company, etc.)
    const subType = v2LeadsMatch[2]; // any subtab type (total, saved, netNew, etc.)
    return {
      basePath: `/v2/leads/${leadType}/${subType}`,
      sequenceId: null,
      viewType: 'leads',
    };
  }

  // Check for leads route: /leads or /leads/:id/:tab
  const leadsMatch = pathname.match(/^\/leads(?:\/(\d+)(?:\/([^/?]+))?)?/);
  if (leadsMatch) {
    return {
      basePath: '/leads',
      sequenceId: null,
      viewType: 'leads',
    };
  }

  // Check for conversations route: /conversations or /conversations/:hashId or /conversations/:hashId/:prospectId/:tab
  // hashId can be alphanumeric, not just digits
  const conversationsMatch = pathname.match(
    /^\/conversations(?:\/([^/]+))?(?:\/(\d+)(?:\/([^/?]+))?)?/,
  );
  if (conversationsMatch) {
    const hashId = conversationsMatch[1] || null;
    // If hashId exists, include it in basePath
    const basePath = hashId ? `/conversations/${hashId}` : '/conversations';
    return {
      basePath,
      sequenceId: null,
      hashId,
      viewType: 'conversations',
    };
  }

  // Check for tasks route: /tasks or /tasks/:id/:tab
  const tasksMatch = pathname.match(/^\/tasks(?:\/(\d+)(?:\/([^/?]+))?)?/);
  if (tasksMatch) {
    return {
      basePath: '/tasks',
      sequenceId: null,
      viewType: 'tasks',
    };
  }

  // Default fallback
  return {
    basePath: '/crm/list',
    sequenceId: null,
    viewType: 'prospects-list',
  };
};

/**
 * Generates the pathname for prospect details modal (without query string or hash)
 * @param pathname - Current pathname
 * @param prospectId - Prospect ID
 * @param tab - Tab name (activity, sequences, contacts, notes, tasks, email)
 * @returns The pathname with prospectId and tab (e.g., /leads/123/activity or /conversations/:hashId/123/activity)
 */
export const generateProspectDetailsUrl = (
  pathname: string,
  prospectId: number,
  tab = 'activity',
): string => {
  const routeInfo = getProspectDetailsRouteInfo(pathname);
  // For conversations route with hashId, the basePath already includes hashId
  // For other routes, basePath is just the base path
  return `${routeInfo.basePath}/${prospectId}/${tab}`;
};

/**
 * Generates the base URL (without prospectId and tab) for closing the modal
 * @param pathname - Current pathname
 * @returns The base URL without prospect details
 */
export const generateBaseUrl = (pathname: string): string => {
  const routeInfo = getProspectDetailsRouteInfo(pathname);
  return routeInfo.basePath;
};

export type ValidTab =
  | 'activity'
  | 'sequences'
  | 'contacts'
  | 'notes'
  | 'tasks'
  | 'email';

/**
 * Extracts the tab from the URL pathname
 * Handles patterns like: /crm/list/:id/:tab, /crm/kanban/:id/:tab, /sequence/:id/prospects/:id/:tab, /leads/:id/:tab, /v2/leads/:tab/:subtab/:id/:tab (dynamic), /conversations/:hashId/:id/:tab, /tasks/:id/:tab
 * @param pathname - Current pathname
 * @returns The tab name if valid, null otherwise
 */
export const extractTabFromUrl = (pathname: string): ValidTab | null => {
  // Match pattern:
  // - /crm/(list|kanban)/:id/:tab
  // - /sequence/:id/prospects/:id/:tab
  // - /leads/:id/:tab
  // - /conversations/:hashId/:id/:tab (hashId can be alphanumeric)
  // - /conversations/:id/:tab (fallback for old pattern without hashId)
  // - /tasks/:id/:tab

  // First try conversations with hashId pattern: /conversations/:hashId/:id/:tab
  const conversationsWithHashIdMatch = pathname.match(
    /^\/conversations\/[^/]+\/\d+\/([^/?]+)/,
  );
  if (conversationsWithHashIdMatch) {
    const tab = conversationsWithHashIdMatch[1];
    if (
      tab === 'activity' ||
      tab === 'sequences' ||
      tab === 'contacts' ||
      tab === 'notes' ||
      tab === 'tasks' ||
      tab === 'email'
    ) {
      return tab as ValidTab;
    }
  }

  // Then try other patterns:
  // - /crm/(list|kanban)/:id/:tab
  // - /sequence/:id/(prospects|emails)/:id/:tab
  // - /v2/leads/:tab/:subtab/:id/:tab (dynamic - matches any tab/subtab combination)
  // - /leads/:id/:tab
  // - /tasks/:id/:tab
  // - /conversations/:id/:tab
  const match = pathname.match(
    /(?:\/crm\/(?:list|kanban)|\/sequence\/\d+\/(?:prospects|emails)|\/v2\/leads\/[^/]+\/[^/]+|\/leads|\/tasks|\/conversations)\/\d+\/([^/?]+)/,
  );
  const tab = match ? match[1] : null;
  if (
    tab === 'activity' ||
    tab === 'sequences' ||
    tab === 'contacts' ||
    tab === 'notes' ||
    tab === 'tasks' ||
    tab === 'email'
  ) {
    return tab as ValidTab;
  }
  return null;
};
